import React from "react"
import moment from "moment-timezone"
import { Loading } from "@common/EcosuiteComponent"
import { ResponsiveContainer, Treemap, Tooltip } from "recharts"
import DateRangeGraph from "../DateRangeGraph"
import i18n from "src/i18n"
import EnergyUtils from "@dashboard/energy/EnergyUtils"

const { t } = i18n

const CUSTOM_COLORS = [
  "#8dd3c7",
  "#ffffb3",
  "#bebada",
  "#fb8072",
  "#80b1d3",
  "#fdb462",
  "#b3de69",
  "#fccde5",
  "#d9d9d9",
  "#bc80bd",
]

// Custom tooltip component for TreeMap
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length > 0) {
    const data = payload[0].payload
    // Format the value using EnergyUtils to display in appropriate units (Wh, kWh, MWh)
    const formattedValue = EnergyUtils.displayWattHours(data.value)

    return (
      <div
        className="treemap-custom-tooltip"
        style={{
          backgroundColor: "white",
          padding: "8px",
          marginTop: "0px",
          border: "1px solid #ccc",
          borderRadius: "4px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <p style={{ margin: "0", fontWeight: "bold" }}>{data.projectName}</p>
        <p style={{ margin: "0", color: "#666" }}>
          Path: {data.name} ({formattedValue})
        </p>
        <p style={{ margin: "0", fontWeight: "bold" }}>
          {formattedValue}
          <span style={{ marginLeft: "8px", fontSize: "13px", color: "#666" }}>({data.percentage.toFixed(1)}%)</span>
        </p>
      </div>
    )
  }

  return null
}

export default class ProjectsTreeMap extends DateRangeGraph {
  renderContent() {
    if (!this.props.datums && !this.props.graphDatums) {
      return <Loading />
    }

    const readings = {}
    for (const project of Object.values(this.props.datums)) {
      for (const [sourceId, source] of Object.entries(project.sources)) {
        if (!sourceId.includes("GEN")) {
          continue
        }

        // /project/site/system/type/id
        // ^-------------------^
        // Get project/site/system and use it as the path
        let path = sourceId.split("/").slice(0, 4).join("/")
        if (!readings[path]) {
          readings[path] = 0
        }

        readings[path] += source.reading
      }
    }

    if (Object.keys(readings).length === 0) {
      return <div className="text-center my-4">{t("common.noDataAvailable")}</div>
    }

    // Calculate total generation across all projects
    const totalGeneration = Object.values(readings).reduce((sum, value) => sum + (value || 0), 0)

    // Transform readings object into array format expected by Treemap
    const data = Object.entries(readings)
      .map(([code, value]) => {
        // Skip null values or they'll cause rendering issues
        if (value === null || value === 0) return null

        // Find the original project object to get additional info
        const projectObj = Object.values(this.props.datums).find((p) => p.code === code)

        // Calculate percentage of total generation
        const percentage = totalGeneration > 0 ? (value / totalGeneration) * 100 : 0

        return {
          name: code,
          value: value,
          lastUpdate: moment().format("YYYY-MM-DD HH:mm"),
          projectName: projectObj?.name || code, // Get the project name if available
          dcSize: projectObj?.dcSize,
          acSize: projectObj?.acSize,
          percentage: percentage,
        }
      })
      .filter((item) => item !== null)

    // Create a root object to wrap the data for proper treemap hierarchy
    const treemapData = [
      {
        name: "Projects",
        children: data,
      },
    ]

    if (data.length === 0) {
      return <div className="text-center my-4">{t("common.noDataAvailable")}</div>
    }

    return (
      <div className="treemap-container" style={{ width: "100%", height: "100%" }}>
        <ResponsiveContainer width="100%" height="100%">
          <Treemap
            data={treemapData}
            dataKey="value"
            aspectRatio={4 / 3}
            stroke="#fff"
            fill="#8884d8"
            animationDuration={500}
            nameKey="name"
            content={<CustomizedContent colors={CUSTOM_COLORS} />}
            height={300}
            tile="squarify"
          >
            <Tooltip
              content={<CustomTooltip />}
              allowEscapeViewBox={{ x: true, y: true }}
              position={{ y: -100 }}
              cursor={{ stroke: "#333", strokeWidth: 1 }}
              wrapperStyle={{ zIndex: 100, pointerEvents: "none" }}
              offset={15}
            />
          </Treemap>
        </ResponsiveContainer>
      </div>
    )
  }
}

// Custom treemap rectangle with project name displayed
function CustomizedContent(props) {
  const { x, y, width, height, index, name, colors, payload } = props

  // Get the color from either the payload or the colors array
  const fillColor =
    (payload && payload.color) || (colors && index !== undefined ? colors[index % colors.length] : "#8884d8")

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        style={{
          fill: fillColor,
          stroke: "#fff",
          strokeWidth: 0,
          strokeOpacity: 1,
        }}
      />
      {width > 30 && height > 30 && (
        <text
          x={x + width / 2}
          y={y + height / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{
            fontSize: Math.min(width / 10, 14),
            fill: "#000",
            fontFamily: "Arial, sans-serif",
            pointerEvents: "none",
            textRendering: "optimizeLegibility",
            paintOrder: "stroke",
            shapeRendering: "crispEdges",
          }}
        >
          {name}
        </text>
      )}
    </g>
  )
}
